// CUSTOMER PO DOCUMENTS ON A QUOTE. STORAGE ONLY (slice A).
//
// A customer sends a purchase order; today it lives in somebody's inbox. This
// attaches it to the quote it answers, lists it, downloads it and removes it.
//
// ⚠️ NOTHING HERE READS A DOCUMENT. No extraction, no comparison against the
// quote's lines, no flagging of mismatches, no auto-creation of anything. That
// is slice B, it needs a human confirmation step, and it does not belong in
// this component.
//
// ⚠️ AND IT MOVES NO NUMBER. Attaching or removing a file does not touch a
// line, a price, a provenance field or the quote's state. The backend proves
// it (test_po_document.py compares every line field either side of an upload);
// this component simply never holds or writes a Material.
//
// ROLES, via the EXISTING helper and no new mechanism:
//   read    everyone who can see the quote, Viewer included.
//   write   useCanEditQuotes() -- Sales or Admin. Sales are who the PO is
//           actually sent to, so gating upload on Admin would put the person
//           who received the document at the back of a queue to file it.
// The backend enforces both; hiding the controls only avoids offering an
// action that would come back 403.

import { useCallback, useEffect, useRef, useState } from "react";
import { FileText, Trash2, Upload } from "lucide-react";
import {
  listQuoteDocuments, uploadQuoteDocument, deleteQuoteDocument,
  quoteDocumentUrl, ACCEPTED_DOCUMENT_TYPES,
  type QuoteDocument,
} from "@/services/api";
import { useCanEditQuotes } from "@/auth/AuthContext";
import { formatTimestampDate } from "@/lib/format";
import {
  colors, semanticColors, radius, spacing, heights, typography,
  iconSize, iconStroke,
} from "@/styles/tokens";

/** Bytes as a short human size. Local rather than in lib/format.ts: it is
 *  used by this one component, and format.ts holds the money/percent/date
 *  helpers the whole app shares. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function PoDocuments({ quoteId, canWrite: mayWriteThisQuote = true }: {
  quoteId: number;
  // Whether the signed-in user may write THIS quote: owner or Admin. The
  // backend has refused anyone else 403 since TASK 2; without this the upload
  // and delete controls were offered to every Sales user on every quote.
  canWrite?: boolean;
}) {
  const canWrite = useCanEditQuotes() && mayWriteThisQuote;

  const [docs, setDocs] = useState<QuoteDocument[]>([]);
  const [status, setStatus] = useState<"loading" | "ok" | "error">("loading");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // ⚠️ WHICH DOCUMENT IS ASKING "ARE YOU SURE" (TASK 3). Removing a file was
  // one click. The trash button now opens a confirmation that NAMES the file;
  // the delete request is sent from that confirmation and nowhere else.
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    setStatus("loading");
    setError("");
    const result = await listQuoteDocuments(quoteId);
    if (!result.ok) {
      // Cleared rather than left stale: a list that failed to refresh is not
      // evidence of what is attached now.
      setDocs([]);
      setError(result.error ?? "Could not load the documents.");
      setStatus("error");
      return;
    }
    setDocs(result.documents ?? []);
    setStatus("ok");
  }, [quoteId]);

  useEffect(() => { void load(); }, [load]);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // The input is reset immediately so picking the SAME file twice still
    // fires a change event -- otherwise a failed upload cannot be retried
    // without choosing a different file first.
    e.target.value = "";
    if (!file) return;

    setBusy(true);
    setError("");
    const result = await uploadQuoteDocument(quoteId, file);
    setBusy(false);

    if (!result.ok) {
      // ⚠️ THE SERVER'S OWN SENTENCE, VERBATIM. It names what IS accepted and
      // that the file's contents were checked; rewording it here would put
      // the same refusal in two places and let them drift.
      setError(result.error ?? "The upload failed.");
      return;
    }
    void load();
  }

  async function onDelete(doc: QuoteDocument) {
    setConfirmDeleteId(null);
    setBusy(true);
    setError("");
    const result = await deleteQuoteDocument(quoteId, doc.id);
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? "The document could not be removed.");
      return;
    }
    void load();
  }

  return (
    <div
      data-testid="po-documents"
      style={{
        border: `1px solid ${semanticColors.border}`,
        borderRadius: radius.base,
        backgroundColor: semanticColors.panelBg,
        padding: spacing[12],
        marginBottom: spacing[12],
      }}
    >
      <div className="flex items-center" style={{ gap: spacing[8], marginBottom: spacing[8] }}>
        <FileText size={iconSize.md} strokeWidth={iconStroke.regular} color={colors.indigo600} />
        <span style={{
          fontSize: typography.fontSize.base,
          fontWeight: typography.fontWeight.medium,
          color: semanticColors.textPrimary,
        }}>
          Customer PO
        </span>
        <span
          data-testid="po-documents-count"
          className="tabular-nums"
          style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}
        >
          {status === "ok" ? docs.length : "…"}
        </span>

        <span style={{ flex: 1 }} />

        {canWrite && (
          <>
            {/* The real control is the hidden input; the button drives it, so
                the file dialog is opened by a styled button rather than the
                browser's default file widget. */}
            <input
              ref={fileRef}
              data-testid="po-documents-file"
              type="file"
              accept={ACCEPTED_DOCUMENT_TYPES}
              onChange={onPick}
              style={{ display: "none" }}
            />
            <button
              data-testid="po-documents-upload"
              className="btn-primary"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
              style={{
                height: heights.control, padding: `0 ${spacing[12]}px`,
                display: "flex", alignItems: "center", gap: spacing[6],
                backgroundColor: colors.indigo600, color: semanticColors.onAccent,
                border: "none", borderRadius: radius.base,
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.medium,
                cursor: busy ? "not-allowed" : "pointer",
                opacity: busy ? 0.6 : 1,
              }}
            >
              <Upload size={iconSize.sm} strokeWidth={iconStroke.regular} />
              {busy ? "Uploading…" : "Attach PO"}
            </button>
          </>
        )}
      </div>

      {/* ⚠️ SAYS WHAT THIS DOES AND DOES NOT DO. Without it the obvious
          reading of an upload box on a quote screen is that the system will
          now check the PO against the quote -- which is slice B and does not
          exist. A stored file that somebody believes was checked is worse
          than no file at all. */}
      <div
        data-testid="po-documents-note"
        style={{
          fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
          marginBottom: spacing[8],
        }}
      >
        Attach the customer's PO PDF to this quote. To read its lines, use
        “Extract from PDF” in PO check below: it runs on our own server, and
        nothing is used until a person confirms each row.
      </div>

      {error && (
        <div
          data-testid="po-documents-error"
          style={{
            fontSize: typography.fontSize.sm, color: colors.red600,
            backgroundColor: colors.red50,
            border: `1px solid ${colors.red100}`,
            borderRadius: radius.base,
            padding: spacing[8], marginBottom: spacing[8],
          }}
        >
          {error}
        </div>
      )}

      {status === "loading" && (
        <div data-testid="po-documents-loading"
             style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
          Loading documents…
        </div>
      )}

      {status === "ok" && docs.length === 0 && (
        <div data-testid="po-documents-empty"
             style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
          {canWrite
            ? "No PO attached yet. Attach the customer's purchase order when it arrives."
            : "No PO attached yet."}
        </div>
      )}

      {status === "ok" && docs.map((d) => (
        <div key={d.id}>
        <div
          data-testid={`po-document-${d.id}`}
          className="flex items-center"
          style={{
            gap: spacing[8], padding: `${spacing[6]}px 0`,
            borderTop: `1px solid ${semanticColors.border}`,
            fontSize: typography.fontSize.sm,
          }}
        >
          {/* ⚠️ A PLAIN LINK, AND download IS SET. The server already forces
              Content-Disposition: attachment, so the file is never rendered
              in this origin; this makes the intent obvious in the markup too.
              A top-level GET carries the SameSite=Lax session cookie, which
              is what lets a link reach a role-gated route at all. */}
          <a
            data-testid={`po-document-link-${d.id}`}
            href={quoteDocumentUrl(quoteId, d.id)}
            download={d.filename}
            rel="noopener noreferrer"
            style={{ color: semanticColors.accentTextOnTint, textDecoration: "none", flex: 1, minWidth: 0,
                     overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {d.filename}
          </a>
          <span className="tabular-nums" style={{ color: semanticColors.textSecondary }}>
            {formatBytes(d.size_bytes)}
          </span>
          <span style={{ color: semanticColors.textMuted }}>
            {formatTimestampDate(d.uploaded_at)}
          </span>
          {/* WHO, as recorded from the session. "unknown" for a file from
              before attribution existed -- never a guess, and never the
              superuser the routes write as. */}
          <span data-testid={`po-document-actor-${d.id}`} style={{ color: semanticColors.textMuted }}>
            Uploaded by {d.uploaded_by ?? "unknown"}
          </span>
          {canWrite && (
            <button
              data-testid={`po-document-delete-${d.id}`}
              aria-label={`Remove ${d.filename}`}
              title="Remove this document"
              disabled={busy}
              aria-expanded={confirmDeleteId === d.id}
              // Opens the confirmation. It does NOT delete.
              onClick={() => setConfirmDeleteId(d.id)}
              style={{
                width: iconSize.lg, height: iconSize.lg, padding: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "transparent", border: "none",
                color: semanticColors.textPlaceholder,
                cursor: busy ? "not-allowed" : "pointer",
              }}
            >
              <Trash2 size={iconSize.sm} strokeWidth={iconStroke.regular} />
            </button>
          )}
        </div>
        {/* THE CONFIRMATION. Names the file -- a generic "are you sure" is
            the one that gets clicked through. */}
        {canWrite && confirmDeleteId === d.id && (
          <div
            data-testid={`po-document-delete-confirm-${d.id}`}
            role="alertdialog"
            aria-label={`Confirm deleting ${d.filename}`}
            className="flex items-center"
            style={{
              gap: spacing[8], flexWrap: "wrap", marginBottom: spacing[6],
              padding: `${spacing[6]}px ${spacing[8]}px`, borderRadius: radius.base,
              backgroundColor: colors.red50, border: `1px solid ${colors.red100}`,
              fontSize: typography.fontSize.sm, color: colors.red600,
            }}
          >
            <span style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
              Delete <strong>{d.filename}</strong> from this quote? This cannot be undone.
            </span>
            <button
              data-testid={`po-document-delete-cancel-${d.id}`}
              onClick={() => setConfirmDeleteId(null)}
              disabled={busy}
              style={{
                height: heights.control, padding: `0 ${spacing[10]}px`,
                backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
                border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                fontSize: typography.fontSize.sm, cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              data-testid={`po-document-delete-yes-${d.id}`}
              onClick={() => void onDelete(d)}
              disabled={busy}
              style={{
                height: heights.control, padding: `0 ${spacing[10]}px`,
                backgroundColor: colors.red600, color: colors.white, border: "none",
                borderRadius: radius.base, fontSize: typography.fontSize.sm, cursor: "pointer",
              }}
            >
              Delete file
            </button>
          </div>
        )}
        </div>
      ))}
    </div>
  );
}
